import {
  Button,
  ButtonGroup,
  Modal,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { type ReactNode, useEffect, useState } from 'react';

/**
 * A confirm for an action that can break things. Unlike CommonConfirmationModal it waits for
 * the action: `onConfirm` resolving `false` keeps the dialog open (the caller shows why in
 * `children`), so a failed delete is never reported as done.
 */
export function DestructiveDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
  testId,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  onConfirm: () => Promise<boolean>;
  testId?: string;
}) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const done = await onConfirm();
    if (!done) setBusy(false);
  };

  return (
    <Modal
      open={open}
      size="medium"
      stayOnClickOutside
      test-id={testId}
      onClose={() => !busy && onCancel()}
    >
      <TextTitle size="small" slot="header">
        {title}
      </TextTitle>
      <Stack direction="column" gutter="small">
        {children}
      </Stack>
      <ButtonGroup slot="footer">
        <Button variant="secondary" disabled={busy} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant="danger"
          loading={busy}
          test-id={testId ? `${testId}-confirm` : undefined}
          onClick={() => void confirm()}
        >
          {confirmLabel}
        </Button>
      </ButtonGroup>
    </Modal>
  );
}
